// Number/time formatting for a Vietnamese UI: "." groups thousands and ","
// is the decimal separator (vi-VN). Rates use SI suffixes (K/M/G), which
// operators read the same in any locale.

const intFmt = new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 });
const dec1 = new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 1, minimumFractionDigits: 0 });
const dec2 = new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });

export function formatInt(n: number): string {
  return intFmt.format(n);
}

const SI = ["", "K", "M", "G", "T", "P"];

/** 1234 -> "1,2 K", 5_600_000 -> "5,6 M"; small values stay exact. */
export function formatSI(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs < 1000) return abs < 10 && abs % 1 !== 0 ? dec1.format(n) : intFmt.format(n);
  let i = 0;
  let v = n;
  while (Math.abs(v) >= 1000 && i < SI.length - 1) {
    v /= 1000;
    i++;
  }
  const s = Math.abs(v) < 10 ? dec2.format(v) : Math.abs(v) < 100 ? dec1.format(v) : intFmt.format(v);
  return `${s} ${SI[i]}`;
}

/** Packets per second -> "1,2 Kpps" / "950 pps". */
export function formatPps(n: number): string {
  const s = formatSI(n);
  return /\s/.test(s) ? `${s}pps` : `${s} pps`;
}

/** Bits per second -> "850 Mbps". */
export function formatBps(bitsPerSec: number): string {
  const s = formatSI(bitsPerSec);
  return /\s/.test(s) ? `${s}bps` : `${s} bps`;
}

export function formatPercent(ratio: number): string {
  return `${dec1.format(ratio * 100)}%`;
}

const dateTime = new Intl.DateTimeFormat("vi-VN", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour12: false,
});
const timeOnly = new Intl.DateTimeFormat("vi-VN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
const dateOnly = new Intl.DateTimeFormat("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });

/** Calendar date only, for day-granular values like a license expiry. */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateOnly.format(d);
}

export function formatDateTime(ms: number | string | undefined): string {
  if (ms === undefined) return "—";
  const d = typeof ms === "string" ? new Date(ms) : new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : dateTime.format(d);
}

export function formatTime(ms: number): string {
  return timeOnly.format(new Date(ms));
}

/** 3725 -> "1 giờ 2 phút"; 45 -> "45 giây". */
export function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  if (s < 60) return `${s} giây`;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d} ngày`);
  if (h) parts.push(`${h} giờ`);
  if (m) parts.push(`${m} phút`);
  if (!d && !h && sec) parts.push(`${sec} giây`);
  return parts.slice(0, 2).join(" ");
}

/** "vừa xong", "5 phút trước", ... relative to now. */
export function formatAgo(ms: number, now = Date.now()): string {
  const diff = Math.round((now - ms) / 1000);
  if (diff < 5) return "vừa xong";
  return `${formatDuration(diff)} trước`;
}
